// Conversations are pairs of question and answer messages.

import React from 'react';
import { type ExpandedLayout, isHiddenMarker } from '../config/constants';
import { useChatLanguage } from '../context/ChatThemeContext';
import { useAnswerAddon } from '../context/PreviewHostContext';
import type { AssistantMessage, UserMessage } from '../services/chatStorage';
import { extractCTAPlaceholders } from '../utils/content/ctaReplacer';
import { getMessage } from '../utils/content/messageTranslations';
import { ContentRecommendation, parseContentRecommendation } from './ContentRecommendationCard';
import EvaluationButtons from './EvaluationButtons';
import QuestionBubble from './ExpandedChat/QuestionBubble';
import { FollowUpQuestions } from './FollowUpQuestions';
import InlineDemoCTAButton from './InlineDemoCTAButton';
import MixedContentRenderer from './MixedContentRenderer';
import { ProfilingProgress } from './ProfilingProgress';
import { SuggestedAnswers } from './SuggestedAnswers';
import ThinkingAnimation from './ThinkingAnimation';

// Re-exported from its declaration in config/constants (beside the markers it
// tests) so non-React callers can share the same visibility rule.
export { isHiddenMarker };

interface ConversationPairProps {
    pair: {
        question: UserMessage | undefined;
        answer: AssistantMessage | undefined;
    };
    index: number;
    primaryColor: string;
    evaluationEnabled: boolean;
    isLoading: boolean;
    isLastPair: boolean;
    domain: string;
    onQuestionRef?: (id: string, ref: HTMLElement | null) => void;
    suggestedFollowUps?: string[];
    suggestedAnswers?: string[];
    profilingProgress?: AssistantMessage['profilingProgress'];
    onFollowUpClick?: (question: string) => void;
    onSuggestedAnswerClick?: (answer: string) => void;
    onRetry?: () => void;
    hideInlineCTAs?: boolean;
    viewLayout?: ExpandedLayout;
    // Inline demo CTA props (shown when 👇 emoji detected in last message)
    showInlineDemoCTA?: boolean;
    isCtaPage?: boolean;
    inlineCtaOptionEnabled?: boolean;
    visitorEmail?: string | null;
    onBookingFlowStart?: (
        ctaId: string,
        redirectUrl: string,
        bookingMessage: string,
        ctaAction?: 'navigate' | 'popup' | 'in-chat'
    ) => void;
    hideInlineProfilingProgress?: boolean;
}

// Helper function to check if follow-up questions should be hidden
const shouldHideFollowUpQuestions = (content: string): boolean => {
    // Hide follow-up questions if content includes specific emojis or bare URLs
    const hideTriggers = ['👉', '👇', '💌'];
    const hasEmoji = hideTriggers.some((emoji) => content.includes(emoji));
    // Strip markdown links before checking for URLs, so only bare URLs suppress follow-ups
    const withoutMarkdownLinks = content.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
    const hasUrl = /https?:\/\//.test(withoutMarkdownLinks);
    return hasEmoji || hasUrl;
};

const removeCTAPlaceholders = (content: string) => {
    // Remove all CTA placeholders from text (inline CTA buttons are no longer rendered)
    const placeholders = extractCTAPlaceholders(content);

    if (placeholders.length === 0) {
        return content;
    }

    let result = content;
    placeholders.forEach((placeholder) => {
        result = result.replace(
            new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            ''
        );
    });

    return result;
};

const ConversationPair: React.FC<ConversationPairProps> = ({
    pair,
    primaryColor,
    evaluationEnabled,
    isLoading,
    isLastPair,
    domain,
    onQuestionRef,
    suggestedFollowUps,
    suggestedAnswers,
    profilingProgress,
    onFollowUpClick,
    onSuggestedAnswerClick,
    onRetry,
    viewLayout = 'expanded',
    hideInlineCTAs,
    showInlineDemoCTA,
    isCtaPage,
    inlineCtaOptionEnabled,
    visitorEmail,
    onBookingFlowStart,
    hideInlineProfilingProgress,
}) => {
    const userLanguage = useChatLanguage();
    // Empty on a client site: only internal surfaces install an addon renderer.
    const answerAddon = useAnswerAddon(pair.answer?.id);

    return (
        <div className="inboundx-chat-messages-pair-container w-full mt-0">
            {/* Question display - hidden for form assistant welcome marker */}
            {pair.question && !isHiddenMarker(pair.question.content) && (
                <QuestionBubble
                    content={pair.question.content}
                    questionId={pair.question.id}
                    onRef={onQuestionRef}
                    viewLayout={viewLayout}
                />
            )}

            {/* Answer section - no container */}
            {pair.answer ? (
                <div
                    className="inboundx-chat-messages-answer-container mb-1.5"
                    // EU AI Act art. 50(2): machine-readable marking of synthetic
                    // text. The visible counterpart is <ChatDisclaimer />.
                    data-ai-generated="true"
                >
                    {/* Show thinking animation when streaming and thinking */}
                    {pair.answer.isStreaming && pair.answer.isThinking ? (
                        <div className="mb-2.5">
                            <ThinkingAnimation isVisible={true} />
                        </div>
                    ) : (
                        (() => {
                            const {
                                mainContent: contentWithoutRecommendation,
                                trailingContent,
                                recommendation,
                            } = parseContentRecommendation(pair.answer.content);
                            const mainContent = removeCTAPlaceholders(contentWithoutRecommendation);
                            // Anything written after the card — the 👉 discovery question — so the
                            // close sits directly above the chips that answer it.
                            const afterCardContent = removeCTAPlaceholders(trailingContent);

                            return (
                                <>
                                    {mainContent && (
                                        <>
                                            <div
                                                className={`mb-2.5 max-w-full break-words ${
                                                    pair.answer?.isError
                                                        ? 'bg-red-50 border border-red-200 rounded-lg pt-3 pl-3 text-red-600'
                                                        : ''
                                                }`}
                                            >
                                                <MixedContentRenderer content={mainContent} />
                                            </div>

                                            {/* Retry button for error messages */}
                                            {pair.answer?.isError && onRetry && (
                                                <div className="mt-3 mb-2">
                                                    <button
                                                        type="button"
                                                        onClick={onRetry}
                                                        disabled={isLoading}
                                                        className="px-4 py-2 text-white font-medium transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        style={{
                                                            borderRadius: 'var(--rw-radius-cta)',
                                                            backgroundColor:
                                                                'var(--rw-bg-action-primary)',
                                                            color: 'var(--rw-text-on-action-primary)',
                                                            fontFamily: 'var(--rw-font-family-cta)',
                                                            fontWeight:
                                                                'var(--rw-font-weight-cta-retry)',
                                                        }}
                                                    >
                                                        {getMessage('retry_button', userLanguage)}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {recommendation && !pair.answer.isStreaming && (
                                        <ContentRecommendation
                                            recommendation={recommendation}
                                            primaryColor={primaryColor}
                                        />
                                    )}

                                    {afterCardContent && !pair.answer.isStreaming && (
                                        <div className="mt-2.5 mb-2.5 max-w-full break-words">
                                            <MixedContentRenderer content={afterCardContent} />
                                        </div>
                                    )}

                                    {/* Host-provided addon under the answer — the Playground's
                                        knowledge strip. Never present in the widget. */}
                                    {answerAddon && !pair.answer.isStreaming ? (
                                        <div className="mt-2.5 mb-2.5">{answerAddon}</div>
                                    ) : null}

                                    {/* Inline demo CTA button (shown when 👇 emoji detected in last message) */}
                                    {showInlineDemoCTA &&
                                        !hideInlineCTAs &&
                                        !pair.answer.isStreaming && (
                                            <InlineDemoCTAButton
                                                domain={domain}
                                                isCtaPage={isCtaPage}
                                                inlineCtaOptionEnabled={inlineCtaOptionEnabled}
                                                visitorEmail={visitorEmail}
                                                onBookingFlowStart={onBookingFlowStart}
                                            />
                                        )}

                                    {/* Evaluation buttons for bot messages (hidden during streaming) */}
                                    {evaluationEnabled &&
                                        pair.answer.onEvaluate &&
                                        !pair.answer.isStreaming && (
                                            <div className="inboundx-chat-messages-evaluation-buttons mb-0 mt-3">
                                                <EvaluationButtons
                                                    onEvaluate={pair.answer.onEvaluate}
                                                    isEvaluated={pair.answer.isEvaluated || false}
                                                    evaluationRating={pair.answer.evaluationRating}
                                                />
                                            </div>
                                        )}

                                    {/* Post-conversion progress dots (hidden when top bar is active) */}
                                    {isLastPair &&
                                        !pair.answer.isStreaming &&
                                        !hideInlineProfilingProgress &&
                                        profilingProgress &&
                                        !profilingProgress.is_complete && (
                                            <ProfilingProgress
                                                currentQuestion={profilingProgress.current_question}
                                                totalFields={profilingProgress.total_fields}
                                                primaryColor={primaryColor}
                                            />
                                        )}

                                    {/* Suggested answers (shown whenever the backend provides them) */}
                                    {isLastPair &&
                                        !pair.answer.isStreaming &&
                                        suggestedAnswers &&
                                        suggestedAnswers.length > 0 &&
                                        onSuggestedAnswerClick && (
                                            <SuggestedAnswers
                                                answers={suggestedAnswers}
                                                onAnswerClick={onSuggestedAnswerClick}
                                            />
                                        )}

                                    {/* Follow-up questions (shown after streaming completes and on last pair only) */}
                                    {/* Hidden when AI asks a question with special emojis (👉, 👇, 💌) or includes URLs */}
                                    {/* Uses the 📎-stripped text (both sides of the card) so recommendation
                                        URLs don't suppress follow-ups, while a 👉 question written after
                                        the card still does — otherwise chips and follow-ups both render. */}
                                    {isLastPair &&
                                        !pair.answer.isStreaming &&
                                        !shouldHideFollowUpQuestions(
                                            `${mainContent}\n${afterCardContent}`
                                        ) &&
                                        suggestedFollowUps &&
                                        suggestedFollowUps.length > 0 &&
                                        onFollowUpClick && (
                                            <FollowUpQuestions
                                                questions={suggestedFollowUps}
                                                onQuestionClick={onFollowUpClick}
                                                viewLayout={viewLayout}
                                                isFormAssistantWelcome={isHiddenMarker(
                                                    pair.question?.content
                                                )}
                                            />
                                        )}
                                </>
                            );
                        })()
                    )}
                </div>
            ) : (
                /* Show thinking animation if we're loading and this is the last pair */
                isLastPair &&
                isLoading && (
                    <div className="inboundx-chat-messages-thinking-animation mb-2.5">
                        <ThinkingAnimation isVisible={true} />
                    </div>
                )
            )}
        </div>
    );
};

export default React.memo(ConversationPair);
